import { makeAutoObservable, reaction, runInAction, type IReactionDisposer } from 'mobx'
import { nanoid } from 'nanoid'
import { type RefObject } from 'react'
import { debounce } from 'lodash'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { type IDropdownItem, toastStore, Avatar, Icon } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { logger } from 'shared/lib'
import {
  type IResponseCannedMessage,
  SavedRepliesSortEnum,
  type ISavedRepliesProps,
  type IParamsGetCannedMessages,
} from 'entities/CannedMessages'
import { IntegrationKey } from 'entities/Integrations'
import { type MessageData } from 'entities/Message'
import { CannedMessage } from 'entities/CannedMessages/model/CannedMessage'
import { type Contact } from 'entities/Contacts/model/Contact'
import { type Integration } from 'entities/Integrations/model/Integration'
import { CannedMessagesApi } from 'entities/CannedMessages/api/savedReplies'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { SavedRepliesContent } from 'widgets/SavedReplies/ui/SavedRepliesContent/SavedRepliesContent'
import { ListSavedRepliesStore } from 'widgets/SavedReplies/store/listSavedRepliesStore'
import { NewSavedReplyStore } from './newSavedReplyStore'
import { type ISavedRepliesStoreConfig } from './types'

export class SavedRepliesStore {
  private _disposeReactionLoadData: IReactionDisposer | null = null
  private _savedRepliesMap = new Map<number, CannedMessage>()
  private _selectedSavedReply: CannedMessage | null = null
  private _selectedSavedReplyRef: RefObject<HTMLButtonElement> | null = null
  private _useScrollToElement = false
  private _contact: Contact | null = null
  private _integration: Integration | null = null
  private _onAddSavedReplies: ((message: MessageData) => void) | null = null
  private _search = ''
  private _loading = false
  private _visibleSavedRepliesActionsId = 0
  private _modalId = ''
  private _leftContentElement: HTMLDivElement | null = null
  private readonly _newSavedReplyStore: NewSavedReplyStore
  private readonly _favoriteListStore: ListSavedRepliesStore
  private readonly _unfavoriteListStore: ListSavedRepliesStore

  constructor(private _entities: IEntitiesStore, config?: ISavedRepliesStoreConfig) {
    this.reactionLoadData()

    this._newSavedReplyStore = new NewSavedReplyStore(
      {
        savedRepliesStore: this,
        mergeFieldProps: config?.mergeFieldProps,
      },
      this._entities
    )
    this._favoriteListStore = new ListSavedRepliesStore({
      filter: 'favorite',
      setSavedReplay: this.setSavedReplay,
    })
    this._unfavoriteListStore = new ListSavedRepliesStore({
      filter: 'unfavorite',
      setSavedReplay: this.setSavedReplay,
    })

    makeAutoObservable(this)
  }

  get leftContentElement() {
    return this._leftContentElement
  }

  get visibleSavedRepliesActionsId() {
    return this._visibleSavedRepliesActionsId
  }

  get selectedSavedReply() {
    return this._selectedSavedReply
  }

  get useScrollToElement() {
    return this._useScrollToElement
  }

  get entities() {
    return this._entities
  }

  get selectedSavedReplyRef() {
    return this._selectedSavedReplyRef
  }

  get savedRepliesPermissionError() {
    return this.canManageSavedReplies ? null : (
      <>
        Contact your admin to enable
        <br />
        saved reply management
      </>
    )
  }

  get canManageSavedReplies() {
    if (this._entities.usersStore.user?.isOwnerRole) return true

    return this._entities.userPermissionsStore.getItem('can_manage_canned_messages')
  }

  get canManageAllSavedReplies() {
    return !!(
      (this.canManageSavedReplies && this._entities.usersStore.user?.isAdminRole) ||
      this._entities.usersStore.user?.isOwnerRole
    )
  }

  get isMySavedReply() {
    return this.selectedSavedReply?.owner_id === this._entities.usersStore.user?.id
  }

  get savedReplyOwner() {
    return this._entities.usersStore.getItem(this.selectedSavedReply?.owner_id)
  }

  get savedReplyVisibilityText() {
    if (!this.newSavedReplyStore.isEdit || this.isMySavedReply) return 'Myself only'

    return this.savedReplyOwner ? `${this.savedReplyOwner.full_name} only` : 'Deleted user only'
  }

  get savedReplyVisibilityIcon() {
    const avatarInfo = this._entities.usersStore.user?.avatarInfo

    if (!this.newSavedReplyStore.isEdit && avatarInfo) return <Avatar info={avatarInfo} size={16} />

    return this.savedReplyOwner ? (
      <Avatar info={this.savedReplyOwner?.avatarInfo} size={16} />
    ) : (
      <Icon icon='userLine' />
    )
  }

  get search() {
    return this._search
  }

  get unfavoriteListStore() {
    return this._unfavoriteListStore
  }

  get favoriteListStore() {
    return this._favoriteListStore
  }

  get modalId() {
    return this._modalId
  }

  get contact() {
    return this._contact
  }

  get newSavedReplyStore() {
    return this._newSavedReplyStore
  }

  setSavedReplay = (savedReplayResponse: IResponseCannedMessage) => {
    const savedReplay = new CannedMessage(savedReplayResponse, this._entities)
    if (this._selectedSavedReply?.id === savedReplay.id) {
      this._selectedSavedReply = savedReplay
    }
    this._savedRepliesMap.set(savedReplay.id, savedReplay)
  }

  setSelectedSavedReplyRef = (ref: React.RefObject<HTMLButtonElement> | null) => {
    this._selectedSavedReplyRef = ref
  }

  setUseScrollToElement = (useScroll: boolean) => {
    this._useScrollToElement = useScroll
  }

  get order(): SavedRepliesSortEnum {
    return this._entities.cannesMessageStore.order
  }

  get paramsGetItems(): IParamsGetCannedMessages {
    return {
      contact_id: this._contact?.id,
      integration_id: this._integration?.id,
      order: this.order,
      search: this._search,
    }
  }

  get loading() {
    return this._favoriteListStore.loading || this._unfavoriteListStore.loading || this._loading
  }

  get savedReplies() {
    const list = Array.from(this._savedRepliesMap.values())

    if (this.order === SavedRepliesSortEnum.letterDown) {
      list.sort((a, b) => {
        if (a.title.toLowerCase() < b.title.toLowerCase()) return -1
        if (a.title.toLowerCase() > b.title.toLowerCase()) return 1
        return 0
      })
    }

    if (this.order === SavedRepliesSortEnum.letterUp) {
      list.sort((a, b) => {
        if (a.title.toLowerCase() > b.title.toLowerCase()) return -1
        if (a.title.toLowerCase() < b.title.toLowerCase()) return 1
        return 0
      })
    }

    if (this.order === SavedRepliesSortEnum.newest) {
      list.sort((a, b) => b.createdAtTimestamp - a.createdAtTimestamp)
    }

    if (this.order === SavedRepliesSortEnum.oldest) {
      list.sort((a, b) => a.createdAtTimestamp - b.createdAtTimestamp)
    }

    return list
  }

  get hasFavorites() {
    return !!this.favoriteSavedReplies.length
  }

  get hasUnfavorites() {
    return !!this.unfavoriteSavedReplies.length
  }

  get hasSavedReplies() {
    return this.hasFavorites || this.hasUnfavorites
  }

  get favoriteSavedReplies() {
    return this.savedReplies.filter((reply) => reply.is_favorite)
  }

  get unfavoriteSavedReplies() {
    return this.savedReplies.filter((reply) => !reply.is_favorite)
  }

  nextItem = () => {
    this._useScrollToElement = true
    const fullList = [...this.favoriteSavedReplies, ...this.unfavoriteSavedReplies]

    if (!this._selectedSavedReply && fullList.length) this._selectedSavedReply = fullList[0]
    if (!this._selectedSavedReply || !fullList.length || fullList.length === 1) return

    const currentIdx = fullList.findIndex((item) => item.id === this._selectedSavedReply?.id)

    if (fullList.length > currentIdx + 1) {
      const item = fullList[currentIdx + 1]
      if (item) this._selectedSavedReply = item
    } else if (fullList.length) {
      this._selectedSavedReply = fullList[0]
    }
  }

  prevItem = () => {
    this._useScrollToElement = true
    const fullList = [...this.favoriteSavedReplies, ...this.unfavoriteSavedReplies]

    if (!this._selectedSavedReply && fullList.length) this._selectedSavedReply = fullList[0]
    if (!this._selectedSavedReply || !fullList.length || fullList.length === 1) return

    const currentIdx = fullList.findIndex((item) => item.id === this._selectedSavedReply?.id)

    if (currentIdx > 0) {
      const item = fullList[currentIdx - 1]
      if (item) this._selectedSavedReply = item
    } else {
      this._selectedSavedReply = fullList[fullList.length - 1]
    }
  }

  private _loadDataWithDebounce = () => {
    this._favoriteListStore.requestLoadData.dispose()
    this._unfavoriteListStore.requestLoadData.dispose()

    runInAction(() => {
      this._loading = true
    })

    this._debounceLoadData()
  }

  private _debounceLoadData = debounce(() => {
    this.loadData()
  }, 500)

  loadData = async () => {
    try {
      runInAction(() => {
        this._loading = true
      })
      this.clear()

      await Promise.all([
        this._favoriteListStore.loadData(this.paramsGetItems),
        this._unfavoriteListStore.loadData(this.paramsGetItems),
      ])

      runInAction(() => {
        const firstSavedReplay = this.favoriteSavedReplies[0] || this.unfavoriteSavedReplies[0]
        this._selectedSavedReply = this.savedReplies.length ? firstSavedReplay : null
      })
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  reset = () => {
    this._disposeReactionLoadData?.()
    this._contact = null
    this._integration = null
    this._onAddSavedReplies = null
    this._search = ''
    this.clear()
    this._favoriteListStore.reset()
    this._unfavoriteListStore.reset()
  }

  clear = () => {
    this._savedRepliesMap.clear()
    this._favoriteListStore.clear()
    this._unfavoriteListStore.clear()
    this._selectedSavedReply = null
  }

  openModal = ({
    contact,
    onAddSavedReplies,
    integration,
    isCurrentAirCall,
  }: ISavedRepliesProps) => {
    this._entities.mergeFieldStore.loadAllFields()
    this.reset()
    this._contact = contact ?? null
    this._integration = integration ?? null
    this._onAddSavedReplies = onAddSavedReplies
    this._modalId = nanoid()
    this.reactionLoadData()
    this._favoriteListStore.reactionLoadData()
    this._unfavoriteListStore.reactionLoadData()

    modalStore.addModal({
      id: this._modalId,
      title: 'Saved replies',
      ModalContentProps: {
        savedRepliesStore: this,
        isCurrentAirCall: isCurrentAirCall,
      },
      ModalContent: SavedRepliesContent,
      pureContent: true,
      width: 720,
    })

    this.loadData()
  }

  closeModal = () => {
    modalStore.closeModal(this._modalId)
    this.reset()
  }

  handleSelectSort = (item: IDropdownItem) => {
    this._entities.cannesMessageStore.setOrder(item.id as SavedRepliesSortEnum)
  }
  setSearch = (search: string) => {
    this._search = search
  }

  setSelectSavedReply = (savedReply: CannedMessage) => {
    this._selectedSavedReply = savedReply
  }

  handleAddSelectedSavedReplies = () => {
    if (this._selectedSavedReply) this.handleAddSavedReplies(this._selectedSavedReply)
  }

  handleAddSavedReplies = (savedReply: CannedMessage | null) => {
    let isDisconnected = false
    const mergeFields = savedReply?.value?.match(/{.*?}/g)

    mergeFields?.forEach((field) => {
      const split = field.replace(/{|}/g, '').trim().split('|')

      const fullMergeField = split[0]
      const splitKey = fullMergeField.split('.')
      const integrationKey = Object.values(IntegrationKey).includes(splitKey[0] as IntegrationKey)
        ? splitKey[0]
        : IntegrationKey.salesmessage

      const integration = this._entities.integrationsStore.getIntegration(integrationKey)
      if (integrationKey !== IntegrationKey.salesmessage && !integration?.isConnected) {
        isDisconnected = true
      }
    })

    if (isDisconnected) {
      const id = nanoid()
      const onReconnect = () => {
        toastStore.remove(id)
        uiStore.changeRoute({
          path: '/settings/integrations',
        })
      }
      toastStore.add({
        id: id,
        title: 'Integration disconnected',
        type: 'error',
        desc: 'To use a saved reply that contains integration merge fields without fallback, please reconnect the integration',
        action: {
          text: 'Reconnect',
          onAction: onReconnect,
        },
      })
    } else {
      if (this._onAddSavedReplies && savedReply) {
        this._onAddSavedReplies({
          message: savedReply.value || savedReply.message,
          attachments: savedReply.attachments,
          isReset: false,
        })
        modalStore.closeModal(this._modalId)
      }
    }
  }

  handleEditSavedReplies = (savedReply: CannedMessage | null) => {
    this._newSavedReplyStore.handleNewSavedReply(savedReply)
  }

  handleDuplicateSavedReplies = (savedReply: CannedMessage | null) => {
    this._newSavedReplyStore.handleNewSavedReply(savedReply, true)
  }

  handleChangeVisibilitySavedReplies = async (
    savedReply: CannedMessage | null,
    isPublic: boolean
  ) => {
    try {
      if (savedReply) {
        const { data } = await CannedMessagesApi.updateCannedMessagesById({
          ...savedReply.updateData,
          public: isPublic,
          contact_id: this._contact?.id,
        })
        if (data) {
          this.setSavedReplay(data)
          this._newSavedReplyStore.setPublic(data.public, true)
        }
        const id = nanoid()
        const onUndo = () => {
          toastStore.remove(id)
          this.handleChangeVisibilitySavedReplies(savedReply, !isPublic)
        }

        const isMySavedReply = this._entities.usersStore.user?.id === savedReply.owner_id
        const savedReplyOwner = this._entities.usersStore.getItem(savedReply.owner_id)

        toastStore.add({
          id: id,
          title: `Now visible ${
            data.public
              ? 'to all members'
              : !isMySavedReply
              ? `only to ${savedReplyOwner ? savedReplyOwner.full_name : 'Deleted user'}`
              : 'only to you'
          }`,
          type: 'info',
          action: {
            text: 'Undo',
            onAction: onUndo,
          },
        })
      }
    } catch (e) {
      logger.error(e)
    }
  }

  setFirstSavedReply = () => {
    const firstSavedReplay = this.favoriteSavedReplies[0] || this.unfavoriteSavedReplies[0]
    this._selectedSavedReply = this.savedReplies.length ? firstSavedReplay : null
    this.handleEditSavedReplies(this._selectedSavedReply)
  }

  onDeleteSavedReply = async (savedReply: CannedMessage) => {
    try {
      const index = this.savedReplies.findIndex((reply) => savedReply.id === reply.id)

      this._savedRepliesMap.delete(savedReply.id)
      if (this._selectedSavedReply?.id === savedReply.id) {
        const nextItem = this.savedReplies[index] || null

        if (nextItem) {
          this._selectedSavedReply = nextItem
          this.handleEditSavedReplies(nextItem)
        } else {
          this.setFirstSavedReply()
        }
      }

      await CannedMessagesApi.deleteCannedMessages(savedReply.id)
    } catch (e) {
      logger.error(e)
    }
  }

  handleDeleteSavedReplies = async (savedReply: CannedMessage | null) => {
    try {
      if (savedReply) {
        const nanoId = nanoid()

        const deleteTimeoutId = setTimeout(async () => {
          toastStore.remove(nanoId)
          this.onDeleteSavedReply(savedReply)
        }, 5000)

        toastStore.add({
          id: nanoId,
          title: 'Saved reply deleted',
          type: 'info',
          action: {
            text: 'Undo',
            onAction: () => {
              clearTimeout(deleteTimeoutId)
              toastStore.remove(nanoId)
            },
          },
        })
      }
    } catch (e) {
      logger.error(e)
    }
  }

  handleFavoriteSavedReplies = async (savedReply: CannedMessage) => {
    try {
      const { data } = await CannedMessagesApi.favoriteCannedMessagesById({
        id: savedReply.id,
        favorite: !savedReply.is_favorite,
      })
      if (data) {
        this.setSavedReplay(data)
      }
    } catch (e) {
      logger.error(e)
    }
  }

  setVisibleSavedRepliesActionsId = (id: number) => {
    this._visibleSavedRepliesActionsId = id
  }

  setLeftContentElement = (leftContentElement: HTMLDivElement) => {
    this._leftContentElement = leftContentElement
  }

  reactionLoadData = () => {
    this._disposeReactionLoadData?.()
    this._disposeReactionLoadData = reaction(() => this.paramsGetItems, this._loadDataWithDebounce)
  }
}
